import React from 'react';
import PropTypes from 'prop-types';
import {
  classNames,
  formatBytes,
  formatDate,
  visibilityBadgeClass,
  visibilityLabel,
  getAttachmentMediaType,
  getAttachmentPreviewUrl,
  getAttachmentMediaUrl,
  getAttachmentVideoMimeType,
} from './helpers';
import AttachmentActions from './AttachmentActions.jsx';

const AttachmentCardView = ({ items, onEdit, onDelete, onPreview, showInstance = false }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((item) => {
      const previewImage = getAttachmentPreviewUrl(item);
      const mediaType = getAttachmentMediaType(item);
      const mediaUrl = getAttachmentMediaUrl(item);
      const videoMimeType = getAttachmentVideoMimeType(item);
      const showPreviewButton = typeof onPreview === 'function';
      const blockPreviewClick = (event) => event.stopPropagation();
      return (
        <div key={item.id} className="flex flex-col bg-white border rounded-lg shadow-sm">
          <button
            type="button"
            onClick={() => onPreview && onPreview(item)}
            disabled={!showPreviewButton}
            className={classNames(
              'relative pt-[56.25%] bg-gray-100 rounded-t-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              !showPreviewButton && 'cursor-default'
            )}
          >
            {mediaType === 'video' && mediaUrl ? (
              <div
                className="absolute inset-0 z-0 bg-black"
                onClick={blockPreviewClick}
                onDoubleClick={blockPreviewClick}
                onPointerDown={blockPreviewClick}
                onPointerUp={blockPreviewClick}
              >
                <video
                  className="h-full w-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                  poster={previewImage || undefined}
                >
                  <source src={mediaUrl} type={videoMimeType} />
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : previewImage ? (
              <img
                src={previewImage}
                alt={item.title || `Attachment #${item.id}`}
                className="absolute inset-0 z-0 h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 z-0 flex items-center justify-center text-gray-400">
                <i className="text-4xl fa fa-file-text-o" />
              </div>
            )}
            {showPreviewButton && (
              <div className="absolute inset-0 z-10 flex items-center justify-center opacity-0 transition-opacity hover:opacity-100 pointer-events-none">
                <span className="inline-flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  {mediaType === 'video' && <i className="fa fa-play" />}
                  {mediaType === 'audio' && <i className="fa fa-volume-up" />}
                  {mediaType === 'image' && <i className="fa fa-search-plus" />}
                  Preview
                </span>
              </div>
            )}
            <div className="absolute top-2 left-2">
              <span className={classNames('px-2 py-1 rounded text-xs font-semibold', visibilityBadgeClass(item))}>
                {visibilityLabel(item)}
              </span>
            </div>
          </button>
          <div className="flex flex-col flex-1 gap-2 p-3">
            <div>
              <div className="text-sm font-semibold truncate" title={item.title}>{item.title || `Attachment #${item.id}`}</div>
              <div className="text-xs text-gray-500 truncate" title={item.description}>{item.description || 'No description'}</div>
            </div>
            <div className="text-xs text-gray-500">
              <div>Size: {formatBytes(item.size_bytes)}</div>
              <div>Type: {item.mime_type || 'Unknown'}</div>
              <div>Updated: {formatDate(item.updated_at)}</div>
            </div>
            {item.tags_list?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {item.tags_list.map((tag) => (
                  <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {showInstance && (
              <div className="text-xs text-gray-500 truncate" title={item.instance?.title || `Instance #${item.instance?.id || '—'}`}>
                Instance: {item.instance?.title || `#${item.instance?.id || '—'}`}
              </div>
            )}
            <div className="text-xs text-gray-600">
              Owner: {item.owner?.name || item.owner_name || '—'}
            </div>
            {showPreviewButton && (
              <div>
                <button type="button" className="btn btn-default btn-xs" onClick={() => onPreview(item)}>
                  Preview
                </button>
              </div>
            )}
            <AttachmentActions attachment={item} onEdit={onEdit} onDelete={onDelete} />
          </div>
        </div>
      );
    })}
  </div>
);

AttachmentCardView.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  showInstance: PropTypes.bool,
};

AttachmentCardView.defaultProps = {
  onPreview: null,
  showInstance: false,
};

export default AttachmentCardView;
